import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Card,
    CardContent,
    Chip,
    CircularProgress,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";
import Button from "@mui/material/Button";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import SearchIcon from "@mui/icons-material/Search";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import _ from "lodash";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import CustomPagination from "app/widgets/Shared/Pagination/CustomPagination";
import { Search, SearchIconWrapper, StyledInputBase } from "app/shared/SearchGlobal/style";
import bugReportServices from "app/services/bug-report-services";
import BugReportFilters from "./BugReportFilters";
import BugReportDetailsDialog from "./BugReportDetailsDialog";
import { STATUS_COLORS, STATUS_LABELS } from "./constants";

const PAGE_SIZE = 20;

const EMPTY_FILTERS = {
    app_version: "",
    screen: "",
    company_id: "",
    from: "",
    to: "",
};

const BugReportsIndex = () => {
    const navigate = useNavigate();
    const { authUser } = useJumboAuth();

    const [listLoading, setListLoading] = useState(false);
    const [records, setRecords] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [openCount, setOpenCount] = useState(0);

    const [searchInput, setSearchInput] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [showFilters, setShowFilters] = useState(false);
    // Se entra a la pantalla para ver lo que falta atender, no el archivo histórico.
    const [status, setStatus] = useState("open");
    const [filters, setFilters] = useState(EMPTY_FILTERS);

    const [detailRecord, setDetailRecord] = useState(null);

    const profileNames = useMemo(
        () => authUser?.profiles?.map((profile) => profile.name) || [],
        [authUser],
    );
    // Solo superadmin, igual que WhatsApp Inbox y Logs. El backend lo exige de todos modos;
    // esto evita mostrar una pantalla que va a devolver 403.
    const canViewModule = useMemo(() => profileNames.includes("superadmin"), [profileNames]);

    useEffect(() => {
        if (authUser && !canViewModule) {
            navigate("/trips");
        }
    }, [authUser, canViewModule, navigate]);

    const buildQueryParams = useCallback(() => {
        const params = new URLSearchParams();

        params.set("page", String(currentPage));
        params.set("per_page", String(PAGE_SIZE));
        if (status) params.set("status", status);
        if (searchQuery) params.set("search", searchQuery);

        Object.entries(filters).forEach(([filterName, filterValue]) => {
            if (filterValue !== undefined && filterValue !== null && String(filterValue).trim() !== "") {
                params.set(filterName, String(filterValue).trim());
            }
        });

        return `?${params.toString()}`;
    }, [currentPage, filters, searchQuery, status]);

    const loadBugReports = useCallback(() => {
        bugReportServices.getBugReports(buildQueryParams())
            .then((response) => {
                setRecords(response.data || []);
                setCurrentPage(response.meta?.current_page || 1);
                setTotalPages(response.meta?.total_pages || 1);
                setTotalCount(response.meta?.total_count || 0);
                setOpenCount(response.meta?.open_count || 0);
            })
            .catch((error) => {
                if (error?.response?.status === 403) {
                    navigate("/trips");
                }
            })
            .finally(() => {
                setListLoading(false);
            });
    }, [buildQueryParams, navigate]);

    useEffect(() => {
        setListLoading(true);
    }, []);

    useEffect(() => {
        if (listLoading) {
            loadBugReports();
        }
    }, [listLoading, loadBugReports]);

    const debouncedSearchChange = useMemo(() => _.debounce((nextValue) => {
        setCurrentPage(1);
        setSearchQuery(nextValue);
        setListLoading(true);
    }, 450), []);

    useEffect(() => () => debouncedSearchChange.cancel(), [debouncedSearchChange]);

    const handleSearchChange = (event) => {
        setSearchInput(event.target.value);
        debouncedSearchChange(event.target.value);
    };

    const handleFiltersChange = (nextFilters) => {
        setFilters(nextFilters);
        setCurrentPage(1);
        setListLoading(true);
    };

    const handleStatusChange = (nextStatus) => {
        setStatus(nextStatus);
        setCurrentPage(1);
        setListLoading(true);
    };

    const clearFilters = () => {
        setFilters(EMPTY_FILTERS);
        setStatus("open");
        setSearchInput("");
        setSearchQuery("");
        setCurrentPage(1);
        setListLoading(true);
    };

    // Reemplaza la fila en memoria en vez de recargar la lista: si se está mirando "Sin resolver"
    // y se acaba de resolver un reporte, recargar lo haría desaparecer de golpe y se pierde la
    // referencia de qué se estaba haciendo. El filtro se aplica en la próxima carga.
    const handleReviewed = (updated) => {
        if (!updated?.id) return;

        setRecords((previous) => previous.map((record) => (
            record.id === updated.id ? { ...record, ...updated } : record
        )));
        setOpenCount((previous) => {
            const wasOpen = ["pending", "in_review"].includes(detailRecord?.status);
            const isOpen = ["pending", "in_review"].includes(updated.status);
            if (wasOpen && !isOpen) return Math.max(previous - 1, 0);
            if (!wasOpen && isOpen) return previous + 1;
            return previous;
        });
    };

    const formatDateTime = (value) => (value ? moment(value).format("DD/MM/YYYY HH:mm") : "-");

    const renderTable = () => {
        if (listLoading) {
            return (
                <Stack alignItems="center" sx={{ py: 8 }}>
                    <CircularProgress color="warning" />
                </Stack>
            );
        }

        if (!records.length) {
            return (
                <Card>
                    <CardContent>
                        <Typography variant="body1" color="text.secondary">
                            No hay reportes con los filtros actuales.
                        </Typography>
                    </CardContent>
                </Card>
            );
        }

        return (
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3, width: "100%" }}>
                <Table size="small" sx={{ width: "100%", tableLayout: "fixed" }}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: "11%" }}>Fecha</TableCell>
                            <TableCell sx={{ width: "10%" }}>Estado</TableCell>
                            <TableCell sx={{ width: "26%" }}>Problema</TableCell>
                            <TableCell sx={{ width: "14%" }}>Quién reportó</TableCell>
                            <TableCell sx={{ width: "13%" }}>Empresa</TableCell>
                            <TableCell sx={{ width: "11%" }}>Pantalla</TableCell>
                            <TableCell sx={{ width: "8%" }}>Versión</TableCell>
                            <TableCell align="right" sx={{ width: "7%" }}>Ver</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {records.map((record) => (
                            <TableRow
                                key={record.id}
                                hover
                                sx={{ cursor: "pointer" }}
                                onClick={() => setDetailRecord(record)}
                            >
                                <TableCell>{formatDateTime(record.created_at)}</TableCell>
                                <TableCell>
                                    <Chip
                                        size="small"
                                        label={STATUS_LABELS[record.status] || record.status}
                                        color={STATUS_COLORS[record.status] || "default"}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Stack direction="row" alignItems="center" spacing={0.75}>
                                        <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
                                            {record.message}
                                        </Typography>
                                        {Boolean(record.screenshots?.length) && (
                                            <Tooltip title={`${record.screenshots.length} captura(s)`}>
                                                <ImageOutlinedIcon fontSize="small" color="action" />
                                            </Tooltip>
                                        )}
                                    </Stack>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2" noWrap>{record.reporter?.name || "-"}</Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2" noWrap>{record.company_name || "-"}</Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2" sx={{ fontFamily: "Monospace", fontSize: 12 }} noWrap>
                                        {record.screen || "-"}
                                    </Typography>
                                </TableCell>
                                <TableCell>{record.app_version || "-"}</TableCell>
                                <TableCell align="right">
                                    <Tooltip title="Ver reporte">
                                        <IconButton
                                            size="small"
                                            color="primary"
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                setDetailRecord(record);
                                            }}
                                        >
                                            <VisibilityOutlinedIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };

    if (authUser && !canViewModule) {
        return null;
    }

    return (
        <React.Fragment>
            <BugReportDetailsDialog
                record={detailRecord}
                onClose={() => setDetailRecord(null)}
                onReviewed={handleReviewed}
            />

            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Typography variant="h2">Reportes de la app</Typography>
                    {openCount > 0 && (
                        <Chip size="small" color="warning" label={`${openCount} sin resolver`} />
                    )}
                </Stack>

                <Stack direction="row" alignItems="center" spacing={1.25} sx={{ flexWrap: "wrap", justifyContent: "flex-end" }}>
                    <Button
                        startIcon={<FilterAltOutlinedIcon />}
                        variant={showFilters ? "contained" : "outlined"}
                        size="small"
                        onClick={() => setShowFilters((previous) => !previous)}
                    >
                        Filtros
                    </Button>

                    <Search sx={{ width: 260 }}>
                        <SearchIconWrapper>
                            <SearchIcon />
                        </SearchIconWrapper>
                        <StyledInputBase
                            placeholder="Buscar en el texto del reporte..."
                            inputProps={{ "aria-label": "search" }}
                            sx={{ background: "white" }}
                            value={searchInput}
                            onChange={handleSearchChange}
                        />
                    </Search>
                </Stack>
            </Stack>

            <BugReportFilters
                open={showFilters}
                filters={filters}
                status={status}
                onFiltersChange={handleFiltersChange}
                onStatusChange={handleStatusChange}
                onClearFilters={clearFilters}
            />

            {renderTable()}

            <CustomPagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalCount={totalCount}
                pageSize={PAGE_SIZE}
                onPageSelect={(page) => {
                    setCurrentPage(page);
                    setListLoading(true);
                }}
            />
        </React.Fragment>
    );
};

export default BugReportsIndex;
